import { useEffect, useState } from 'react';

function useSessionStorage<T>(key: string, setToIfMissing: T) {
  const getValue = () => {
    const storageValue = sessionStorage.getItem(key);
    let v: T;
    if (!storageValue) {
      sessionStorage.setItem(key, JSON.stringify(setToIfMissing));
      v = setToIfMissing;
    } else {
      v = JSON.parse(storageValue) as T;
    }
    return v;
  };

  const [value, setValue] = useState(getValue());

  // Listen to storage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        setValue(JSON.parse(e.newValue) as T);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  // Listen to custom storage event for same-tab changes
  useEffect(() => {
    const handleCustomStorageChange = (e: Event) => {
      const customEvent = e as CustomEvent<{ key: string; newValue: string }>;
      if (customEvent.detail.key === key) {
        setValue(JSON.parse(customEvent.detail.newValue) as T);
      }
    };

    window.addEventListener('session-storage-change', handleCustomStorageChange);
    return () => window.removeEventListener('session-storage-change', handleCustomStorageChange);
  }, [key]);

  const setStorageValue = (newValue: T) => {
    sessionStorage.setItem(key, JSON.stringify(newValue));
    setValue(newValue);

    // Dispatch custom event for same-tab synchronization
    window.dispatchEvent(
      new CustomEvent('session-storage-change', {
        detail: { key, newValue: JSON.stringify(newValue) },
      })
    );
  };

  return { value, setTo: setStorageValue };
}

export default useSessionStorage;
